import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs';
import { earningsSx, MainBox } from '../styles'
import { useSelector } from 'react-redux'
import { FaArrowLeft } from 'react-icons/fa'
import { httpsCallable } from 'firebase/functions'
import StripeLogo from '../../../Assets/Images/stripe.png'
import { functions } from '../../../Infrastructure/config'
import { StripeAccountManagement } from './stripeAccountManagement'
import { Box, Button, Paper, Table, CircularProgress, TableBody, TableContainer, TableRow, Typography, TableHead, useTheme } from '@mui/material'
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import { ErrorView } from '../../Common/ErrorComponents/errorView';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import { MdErrorOutline } from 'react-icons/md';
import countries from '../../../Infrastructure/Constants/countries.json';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';
import { TableHeaderCell } from '../../Common/TableHeaderCell/tableHeaderCell';
import { TableBodyCell } from '../../Common/TableBodyCell/tableBodyCell';

export const Earnings = ({ accountId, stripeAccountStatus, loading, setLoading, setError, error, setCreateStripeAccLoading }) => {
    const theme = useTheme()
    const baseURL = window.location.origin;
    const {userId} = useAuthentication()
    const [currencySymbol, setCurrencySymbol] = useState(null);
    const [earnings, setEarnings] = useState(null)
    const ip = useSelector((state) => state.ip.address)
    const [stripeError, setStripeError] = useState(null)
    const [earningsError, setEarningsError] = useState(null)
    const page = useSelector((state)=> state.route.currentRoute)
    const [earningsLoading, setEarningsLoading] = useState(null)
    const [showAccountManagement, setShowAccountManagement] = useState(null)
    const [transactionDetails, setTransactionsDetails] = useState(null)
    const [transactionDetailsLoading, setTransactionsDetailsLoading] = useState(false)
    const userData = useSelector((state) => state.userData.data)
    const firstName = userData?.account?.first_name;
    const lastName = userData?.account?.last_name;
    const pendingBalance = earnings?.balance?.pending[0]?.amount ?
        (earnings.balance.pending[0].amount / 100).toFixed(2) : '0.00';
    const availableBalance = earnings?.balance?.available[0]?.amount ?
        (earnings.balance.available[0].amount / 100).toFixed(2) : '0.00';
    const payouts = earnings?.payouts ? (earnings.payouts / 100).toFixed(2) : 0.00;
    const country = stripeError && stripeError.country && (
        countries.find(country => country.alpha_code === stripeError.country)?.label || stripeError.country
      )
    const connectAccount = async () => {
        try {
            setLoading(true)
            setCreateStripeAccLoading(true)
            const connectStripeAccount = httpsCallable(functions, "setupstripeaccount");
            const res = await connectStripeAccount({ firstName, lastName, app_base_url: baseURL, ip, page });
            if (res?.data?.url) {
                window.location.href = res.data.url;
                return
            } else if (res?.data?.errorMessage){
                setStripeError(res?.data)
                setLoading(false)
            }
        } catch (error) {
            setLoading(false)
            setCreateStripeAccLoading(false)
            setEarningsError(ERROR_CONSTANTS.GENERIC_ERROR_VIEW)
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.EARNINGS_STRIPE_CONNECT_FAILED,
              uid: userId || null,
              source: "earnings.jsx",
              emailInfo: null,
              metadata: null,
              error: error?.message || null
            });
        }
    }
    const stripeAccountDetails = () => {
        setShowAccountManagement(true)
    }
    const getStatus = (status) => {
        switch (status) {
            case 'succeeded':
                return 'succeeded';
            case 'requires_payment_method':
                return 'failed';
            case 'refunded':
                return 'refunded';
            case 'processing':
                return 'pending';
            default:
                return 'pending';
        }
    }
    async function getCurrencySymbol(code) {
        try {
            const res = await fetch(`https://restcountries.com/v3.1/currency/${code}`);
            const data = await res.json();
            if (Array.isArray(data) && data[0]?.currencies?.[code]) {
            return data[0].currencies[code].symbol || code;
            }
        } catch {
            return code;
        }
        return code;
    }
    useEffect(() => {
        async function fetchData() {
            try {
                setEarningsLoading(true)
                const getAccountBalance = httpsCallable(functions, "geteducatoraccbalance");
                const res = await getAccountBalance({ ip, page });
                setEarnings({ balance: res?.data?.balance, payouts: res?.data?.payouts })
            } catch (error) {
                setEarningsError(ERROR_CONSTANTS.GENERIC_ERROR_VIEW)
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.EARNINGS_BALANCE_FETCH_FAILED,
                  uid: userId || null,
                  source: "earnings.jsx",
                  emailInfo: null,
                  metadata: { accountId },
                  error: error?.message || null
                });    
            } finally {
                setEarningsLoading(false)
            }
        }
        if (accountId && userId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, userId]);

    useEffect(() => {
        async function fetchData() {
            try {
                setTransactionsDetailsLoading(true)
                const getTransactions = httpsCallable(functions, "educatorpayments");
                const res = await getTransactions({ stripe_account_id: accountId, ip, page });
                setTransactionsDetails(res?.data?.paymentIntents)
            } catch (error) {
                setEarningsError(ERROR_CONSTANTS.GENERIC_ERROR_VIEW)
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.EARNINGS_PAYMENTS_FETCH_FAILED,
                  uid: userId || null,
                  source: "earnings.jsx",
                  emailInfo: null,
                  metadata: { accountId },
                  error: error?.message || null
                });    
            } finally {
                setTransactionsDetailsLoading(false)
            }
        }
        if (accountId && userId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, userId])

    const handleTotalAmount = (amount, fee) => {
        const totalAmount = (amount - fee) / 100;
        return totalAmount;
    }
    const currencyCode = earnings?.balance?.available?.[0]?.currency?.toUpperCase() || null;
    useEffect(() => {
        async function fetchSymbol() {
            if (currencyCode) {
                setCurrencySymbol(null)
                const symbol = await getCurrencySymbol(currencyCode)
                setCurrencySymbol(symbol)
            }
        }
        fetchSymbol()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyCode])
    return (
        <MainBox>
            {
                showAccountManagement ?
                    <Box>
                        <Box sx={earningsSx.stripeDetailsBox}>
                            <Box sx={earningsSx.stripeDetailsIcon}>
                                <FaArrowLeft
                                    fontSize={25}
                                    onClick={() => setShowAccountManagement(false) }
                                />
                            </Box>
                            <Box sx={earningsSx.stripeDetailsTextBox}>
                                <Typography variant='h1' sx={earningsSx.stripeDetailsText}>Stripe Details</Typography>
                            </Box>
                        </Box>
                        {(
                            error ? <Box><ErrorView/></Box> :
                        <StripeAccountManagement accountId={accountId} error={error} setError={setError} userId={userId} loading={loading} setLoading={setLoading} />
                        )}
                    </Box>
                    :
                    <Box>
                        <Box sx={earningsSx.pageHeaderBox}>
                            <Typography variant='h1'>Earnings</Typography>
                        </Box>
                        {
                            loading
                        ? 
                        <Box sx={earningsSx.loadingWrapper}>
                            <CircularProgress />
                            <Typography color="text.secondary" sx={earningsSx.loadingText}>
                                Loading may take several minutes
                            </Typography>
                        </Box>
                        : earningsError ? <Box><ErrorView/></Box>
                            : stripeError?.errorMessage ?
                                <Box sx={earningsSx.stripeErrorWrapper}>
                                    <MdErrorOutline size={70} style={{ color: theme.palette.primary.main }} />
                                    <Typography variant="h5" color="primary" sx={earningsSx.stripeErrorTitle}>
                                        Oops! Something went wrong.
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={earningsSx.stripeErrorDescription}>
                                        {stripeError.errorMessage}{" "}{country}
                                    </Typography>
                                </Box>
                                :
                                <Box sx={earningsSx.earningsWrapper}>
                                    {
                                        stripeAccountStatus === "completed" ?
                                            <Box sx={earningsSx.earningsCard}>
                                                <Box sx={earningsSx.earningsTopRow}>
                                                    <Box sx={earningsSx.earningsStatsGroup}>
                                                        <Box sx={earningsSx.earningsStatItem}>
                                                            <Typography variant="body1" color="initial" sx={earningsSx.statLabel}>Total Payouts</Typography>
                                                            {earningsLoading ? (
                                                                <CircularProgress size={24} />
                                                            ) : (
                                                                <Typography variant="body1" color="initial" sx={earningsSx.statValue}>{currencySymbol}{" "}{payouts}</Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={earningsSx.earningsStatItem}>
                                                            <Typography variant="body1" color="initial" sx={earningsSx.statLabel}>Current Balance</Typography>
                                                            {earningsLoading ? (
                                                                <CircularProgress size={24} />
                                                            ) : (
                                                                <Box>
                                                                    <Typography variant="body1" color="initial" sx={earningsSx.statValue}>{currencySymbol}{" "}{availableBalance}</Typography>
                                                                    <Typography variant="body1" color="grey" fontSize={12}>{currencySymbol}{" "}{pendingBalance} Pending</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box>
                                                        {loading ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <Button sx={earningsSx.stripeDetailsButton} variant="contained" color="primary" onClick={stripeAccountDetails}>
                                                                Show Stripe Details
                                                            </Button>
                                                        )}

                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body1" color="initial" sx={earningsSx.recentEnrollmentsTitle}>Recent Enrollments</Typography>
                                                    {transactionDetailsLoading ? (
                                                        <Box sx={earningsSx.transactionsLoader}>
                                                            <CircularProgress />
                                                        </Box>
                                                    ) : (
                                                        <TableContainer component={Paper}>
                                                            {transactionDetails?.length > 0 ? (
                                                                <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableHeaderCell sx={{ ...earningsSx.earningsTableSx.headerCell, pl: 7, minWidth: 200 }}>
                                                                                Cohort ID
                                                                            </TableHeaderCell>
                                                                            <TableHeaderCell sx={{ ...earningsSx.earningsTableSx.headerCell, pl: 7, minWidth: 200 }}>
                                                                                Course Title
                                                                            </TableHeaderCell>
                                                                            <TableHeaderCell sx={earningsSx.earningsTableSx.headerCell}>
                                                                                Date
                                                                            </TableHeaderCell>
                                                                            <TableHeaderCell sx={earningsSx.earningsTableSx.headerCell}>
                                                                                Amount
                                                                            </TableHeaderCell>
                                                                            <TableHeaderCell sx={{ ...earningsSx.earningsTableSx.headerCell, minWidth: 200 }}>
                                                                                Payment Status
                                                                            </TableHeaderCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {transactionDetails?.map((transaction, index) => (
                                                                            <TableRow key={index}>
                                                                                <TableBodyCell sx={{ ...earningsSx.earningsTableSx.bodyCell, pl: 7 }}>
                                                                                    {transaction?.metadata?.cohort_id}
                                                                                </TableBodyCell>
                                                                                <TableBodyCell sx={{ ...earningsSx.earningsTableSx.bodyCell, pl: 7 }}>
                                                                                    {transaction?.metadata?.course_title}
                                                                                </TableBodyCell>
                                                                                <TableBodyCell sx={earningsSx.earningsTableSx.bodyCell}>
                                                                                    {dayjs.unix(transaction?.created).format('MM/DD/YYYY')}
                                                                                </TableBodyCell>
                                                                                <TableBodyCell sx={earningsSx.earningsTableSx.bodyCell}>
                                                                                    ${handleTotalAmount(transaction?.amount, transaction?.application_fee_amount)}
                                                                                </TableBodyCell>
                                                                                <TableBodyCell sx={earningsSx.earningsTableSx.bodyCell}>
                                                                                    {getStatus(transaction?.status)}
                                                                                </TableBodyCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            ) : (
                                                                <Typography variant="h5" sx={earningsSx.emptyTransactionsText}>No Transactions Found</Typography>
                                                            )}
                                                        </TableContainer>

                                                    )}
                                                </Box>
                                            </Box>
                                            :
                                            <Box>
                                                <Box sx={earningsSx.onboardingCard}>
                                                    <Typography sx={earningsSx.onboardingText} dangerouslySetInnerHTML={{
                                                        __html: stripeAccountStatus === "not_started" ? "We partnered with Stripe to ensure your bank information is secure and payments are processed reliably. Click <b>Link Stripe Account</b> to setup your course payouts." :
                                                            stripeAccountStatus === "partial" ? "You must complete onboarding through Stripe to continue" : ""
                                                    }}>
                                                    </Typography>
                                                    <Box sx={earningsSx.stripeImageWrapper}>
                                                        <img src={StripeLogo} style={{ position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain'
                                                            }}
                                                            alt="not found"
                                                        />
                                                    </Box>
                                                </Box>
                                                <Button sx={earningsSx.onboardingButton} variant="contained" color="primary" onClick={connectAccount}>
                                                    {stripeAccountStatus === "not_started" ? "Link Stripe Account" : stripeAccountStatus === "partial" ? "Complete Onboarding" : ""}
                                                </Button>
                                            </Box>
                                    }
                                </Box>
                        }
                    </Box>
            }
        </MainBox>
    )
}
